import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MistOrb from "@/components/MistOrb";
import { captureRef, storedRef, storedGift, storedChannel } from "@/lib/referral";
import Logo from "@/components/Logo";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { user, login, register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("email") || ""; } catch { return ""; }
  });
  const [formErr, setFormErr] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [dob, setDob] = useState("");
  const maxDob = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().slice(0, 10); })();

  useEffect(() => { captureRef(); }, []);

  useEffect(() => { if (user) navigate("/app"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && (!dob || dob > maxDob)) {
      setFormErr("Xcelio is for adults aged 18 and over."); return;
    }
    setLoading(true); setFormErr("");
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password, dob, storedRef(), storedGift(), storedChannel());
      navigate("/app");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message || "Something went wrong.";
      setFormErr(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const emailExists = !isLogin && /already exists/i.test(formErr);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative grain items-center justify-center overflow-hidden">
        <MistOrb size={380} state="idle" testId="auth-orb" />
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <p className="font-serif text-4xl leading-tight italic text-foreground/90">The way you enter a room changes when your inner world does.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-sm">
          <Link to="/" className="flex items-center mb-10">
            <Logo className="text-2xl" showDot />
          </Link>
          <h1 className="font-serif text-4xl mb-2">{isLogin ? "Welcome back" : "Let's begin"}</h1>
          <p className="text-muted-foreground mb-8 text-sm">
            {isLogin ? "Continue becoming more yourself." : "In a moment, Xcelio will get to know you."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Your name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="auth-name-input" className="mt-1.5 rounded-full" placeholder="What should Xcelio call you?" />
              </div>
            )}
            {!isLogin && (
              <div>
                <Label htmlFor="dob">Date of birth</Label>
                <Input id="dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} required data-testid="auth-dob-input" className="mt-1.5 rounded-full" />
                <p className="text-[11px] text-muted-foreground mt-1.5 px-1" data-testid="auth-age-note">Xcelio is for adults 18+. We only use this to confirm your age.</p>
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value.trim())} required data-testid="auth-email-input" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="mt-1.5 rounded-full" placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="auth-password-input" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="rounded-full pr-12" placeholder="Your password" />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="auth-show-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {isLogin && (
                <div className="text-right mt-2">
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="auth-forgot-link">Forgot password?</Link>
                </div>
              )}
            </div>
            {formErr && (
              <div className="rounded-2xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200" data-testid="auth-error">
                {formErr}
                {emailExists && (
                  <button type="button" onClick={() => navigate(`/login?email=${encodeURIComponent(email)}`)}
                    className="block mt-2 text-primary font-medium" data-testid="auth-login-instead">
                    Log in to that account instead →
                  </button>
                )}
              </div>
            )}
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="auth-submit-btn">
              {loading ? "Please wait…" : isLogin ? "Log in" : "Continue"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {isLogin ? "New to Xcelio? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-primary" data-testid="auth-switch-link">{isLogin ? "Begin" : "Log in"}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
